import { Component, OnInit, Input, EventEmitter, Output } from '@angular/core';
import { PayrollItemModel } from 'src/app/models/payableItem';
import { getTimeFormatted, deepClone } from 'src/app/helpers/utils';
import { BaseComponent } from 'src/app/core/abstracts/baseComponent';
import { PayrollRejectionReasonModel } from 'src/app/models/payrollRejectionReasonModel';
import { OverlayOptions, SelectItem } from 'primeng/api';

@Component({
    selector: 'app-payroll-reject-items-table',
    templateUrl: './payroll-reject-items-table.component.html',
    styleUrls: ['./payroll-reject-items-table.component.scss'],
    standalone: false
})
export class PayrollRejectItemsTableComponent extends BaseComponent implements OnInit {

  @Input() set payrollItems(value: PayrollItemModel[]) {
    if (value) {
      let totalTimeMinutes = 0;
      value.forEach(payrollItem => {
        totalTimeMinutes += payrollItem.payableItem.timeDiffMinutes;
      });
      this.totalTime = getTimeFormatted(totalTimeMinutes);
    } else {
      this.totalTime = '';
    }
    this._payrollItems = deepClone(value);
    this.initializeRejectionReasonSelections();
  }

  get payrollItems() {
    return this._payrollItems;
  }

  // tslint:disable-next-line:variable-name
  _payrollItems: PayrollItemModel[];

  @Input() payableType: string;
  @Input() title: string;
  @Input() set rejectionReasons(value: PayrollRejectionReasonModel[]) {
    this._rejectionReasons = value || [];
    this.rejectionReasonOptions = this.buildRejectionReasonOptions(this._rejectionReasons);
    this.initializeRejectionReasonSelections();
  }

  get rejectionReasons() {
    return this._rejectionReasons;
  }

  @Output() itemsChanged = new EventEmitter<PayrollItemModel[]>();

  @Input() employeeView: boolean;

  readonly otherReason = 'Other';
  readonly rejectionReasonOverlayBaseZIndex = 111000;
  readonly rejectionReasonOverlayOptions: OverlayOptions = {
    appendTo: 'body',
    autoZIndex: true,
    baseZIndex: this.rejectionReasonOverlayBaseZIndex
  };
  _rejectionReasons: PayrollRejectionReasonModel[] = [];
  rejectionReasonOptions: SelectItem[] = [{ label: this.otherReason, value: this.otherReason }];

  totalTime: string;
  selectedItems = [];
  allSelected: boolean;

  constructor() {
    super();
  }

  ngOnInit() {

  }

  changed() {
    this.itemsChanged.emit(this.payrollItems);
  }

  rejectionReasonChanged(selectedRejectionReason: string | SelectItem, payrollItem: PayrollItemModel) {
    selectedRejectionReason = this.getReasonText(selectedRejectionReason);
    payrollItem.payableItem.selectedRejectionReason = selectedRejectionReason;

    if (!this.isOtherReason(selectedRejectionReason)) {
      payrollItem.payableItem.reason = selectedRejectionReason;
    } else if (this.isConfiguredReason(payrollItem.payableItem.reason)) {
      payrollItem.payableItem.reason = '';
    }

    this.changed();
  }

  isOtherSelected(payrollItem: PayrollItemModel) {
    return this.isOtherReason(this.getSelectedRejectionReason(payrollItem));
  }

  initializeRejectionReasonSelections() {
    if (!this.payrollItems) {
      return;
    }

    this.payrollItems.forEach(payrollItem => {
      const reason = payrollItem.payableItem.reason;
      const selectedReason = this.getSelectedRejectionReason(payrollItem);
      const matchingReason = this._rejectionReasons.find(r =>
        r.isActive && !this.isOtherReason(r.reason) && this.getReasonText(r.reason) === this.getReasonText(reason));

      if (this.isOtherReason(selectedReason)) {
        payrollItem.payableItem.selectedRejectionReason = this.otherReason;
      } else if (matchingReason) {
        payrollItem.payableItem.selectedRejectionReason = this.getReasonText(matchingReason.reason);
      } else if (reason) {
        payrollItem.payableItem.selectedRejectionReason = this.otherReason;
      } else {
        payrollItem.payableItem.selectedRejectionReason = this.rejectionReasonOptions.length === 1 ? this.otherReason : null;
      }
    });
  }

  buildRejectionReasonOptions(reasons: PayrollRejectionReasonModel[]): SelectItem[] {
    const activeReasons = (reasons || []).filter(r => r.isActive);
    if (!activeReasons.some(r => this.isOtherReason(r.reason))) {
      activeReasons.push({
        id: 0,
        reason: this.otherReason,
        sortOrder: 0,
        isActive: true,
        isDefault: true
      });
    }

    return activeReasons
      .sort((a, b) => {
        if (a.isDefault !== b.isDefault) {
          return a.isDefault ? -1 : 1;
        }
        if (a.sortOrder !== b.sortOrder) {
          return a.sortOrder - b.sortOrder;
        }
        return this.getReasonText(a.reason).localeCompare(this.getReasonText(b.reason));
      })
      .map(r => {
        const reason = this.isOtherReason(r.reason) ? this.otherReason : this.getReasonText(r.reason);
        return { label: reason, value: reason };
      });
  }

  private isConfiguredReason(reason: string) {
    return !!reason && this._rejectionReasons.some(r => r.isActive && this.getReasonText(r.reason) === this.getReasonText(reason));
  }

  private getSelectedRejectionReason(payrollItem: PayrollItemModel): string {
    return this.getReasonText(payrollItem?.payableItem?.selectedRejectionReason as any);
  }

  private getReasonText(selectedRejectionReason: string | SelectItem): string {
    if (selectedRejectionReason && typeof selectedRejectionReason === 'object') {
      selectedRejectionReason = (selectedRejectionReason.value || (selectedRejectionReason as any).reason || selectedRejectionReason.label || '') as string;
    }

    const reason = ((selectedRejectionReason || '') as string).trim();
    return this.isOtherReason(reason) ? this.otherReason : reason;
  }

  private isOtherReason(reason: string) {
    return (reason || '').trim().toLowerCase() === this.otherReason.toLowerCase();
  }

  trackByFunction = (index, item) => {
    return index;
  }

}
